import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";

import ScoreScreenSidebar from "../components/score/ScoreScreenSidebar";
import { platformActions, RootStatePlatform } from "../store/platform-slice";
import SystemDetails from "../components/score/SystemDetails";
import { DemoReportMessage, DemoReportOverlay } from "./DashboardScreen";
import BodyMapAndServicesPanel from "../components/score/BodyMapAndServicesPanel";
import useTheme from "../../../common/hooks/useTheme";
import { Theme } from "../../../providers/ThemeProvider";
import TestIncludedCard from "../components/score/TestIncludedCard";
import PrimaryButton from "../../../common/ui/PrimaryButton";
import ActionButton from "../../../common/ui/ActionButton";
import LoadingSpinner from "../../../common/ui/LoadingSpinner";
import { resetApprovals } from "../../../common/network/Api";
import { useAppStore, useConfig } from "../../../providers/AppStore";
import PageLoader from "../../../common/ui/PageLoader";
import { useAuth } from "../../../providers/AuthProvider";
import { useSubject } from "../providers/SubjectProvider";
import {
  isResourcePending,
  usePlatformResource,
} from "../hooks/usePlatformResource";
import { useScoreSidePanel } from "../components/score/ScoreSidePanelContext";
import AllBiomarkersView from "../components/healthOverview/AllBiomarkersView";
import AddResultsMenu from "../components/healthOverview/AddResultsMenu";
import PendingApprovalsBanner from "../components/healthOverview/PendingApprovalsBanner";
import BiomarkerExtractorModal from "../components/biomarkerTab/BiomarkerExtractorModal";
import InBodySyncModal from "../components/biomarkerTab/InBodySyncModal";
import KeragonSyncModal from "../components/keragon/KeragonSyncModal";
import ReviewHealthScoresModal from "../components/clients/ReviewHealthScoresModal";
import EnergyTrainingSystem from "../components/healthOverview/energyTraining/EnergyTrainingSystem";
import { ENERGY_TRAINING_SYSTEM_ID } from "../components/healthOverview/energyTraining/energyTrainingData";

/**
 * Health overview — the former Score screen combined with the Biomarkers
 * capabilities, per the "System detail view" section of the new-platform
 * Figma. Mounted at the same routes as the old Score screen
 * (/client/score and /platform/score), so no old URL changes.
 *
 * Views:
 *  - "systems" (default): system rail + body map; selecting a system shows
 *    the system detail column with its PNOE metrics and biomarkers.
 *  - "all": the All-biomarkers list (search / filters / cards) replacing the
 *    detail + body-map columns.
 *
 * Affiliate-only elements (hidden for role 1 customers): the "+ Add results"
 * menu with the Upload/InBody modals, the pending-approvals banner and the
 * Manage-biomarkers entry inside SystemDetails.
 */
const HealthOverviewScreen = () => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("id");

  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );

  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );

  const config = useConfig();
  const [isResetting, setIsResetting] = useState(false);
  const [view, setView] = useState<"systems" | "all">("systems");
  const [extractorModalOpen, setExtractorModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [ehrModalOpen, setEhrModalOpen] = useState(false);
  // Owned here rather than by the banner: both the banner's "Review" link and
  // the post-save snackbar's "Review scores" action open the same modal.
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const { subjectId } = useSubject();
  const { AuthUser } = useAuth();
  const { ASSelectedClient, ASRefreshResource } = useAppStore();
  const { closePanel } = useScoreSidePanel();
  const isAffiliate = AuthUser?.role !== 1;

  const clientName =
    ASSelectedClient?.name ||
    [ASSelectedClient?.firstname, ASSelectedClient?.lastname]
      .filter(Boolean)
      .join(" ");

  // The overview is report-backed like the old Score screen, plus the
  // biomarkers resource that feeds the All-biomarkers view and side panel.
  const reportStatus = usePlatformResource("report", subjectId, AuthUser.role);
  usePlatformResource("biomarkers", subjectId, AuthUser.role);

  // The rail stays clickable in the All-biomarkers view, where a selection
  // filters the list by system. Energy & Training Zones has no biomarkers to
  // filter by, so picking it there opens its zones in the systems view.
  useEffect(() => {
    if (view === "all" && selectedSystem === ENERGY_TRAINING_SYSTEM_ID) {
      setView("systems");
    }
  }, [view, selectedSystem]);

  const handleReset = async () => {
    if (!clientId) return;
    setIsResetting(true);
    try {
      await resetApprovals(clientId);
      window.location.reload();
    } catch (error) {
      console.error("Error resetting approvals:", error);
      setIsResetting(false);
    }
  };

  if (isResourcePending(reportStatus)) {
    return <PageLoader />;
  }

  return (
    <div style={styles.screenRoot}>
      {/* Floating sample-data notice: absolutely positioned in the empty
          middle of the toolbar row so showing/hiding it never shifts the
          layout. Hidden in the All-biomarkers view, where everything shown
          is the client's real, editable data. */}
      {isDemoReport && view !== "all" && (
        <div style={styles.demoMessageFloat(theme)}>
          <DemoReportMessage />
        </div>
      )}
      <div style={styles.toolbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Sample reports: the Test-included trigger chip renders faded
              (sample data) via `dimmed` — its popover stays fully opaque —
              and the dev-only Reset approvals is hidden (nothing to reset). */}
          <TestIncludedCard dimmed={isDemoReport} />
          {/*{config.ID !== "prod" && !isDemoReport && (*/}
          {/*  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>*/}
          {/*    <PrimaryButton*/}
          {/*      label={"Reset approvals"}*/}
          {/*      isDisable={isResetting}*/}
          {/*      onClick={handleReset}*/}
          {/*    />*/}
          {/*    {isResetting && (*/}
          {/*      <LoadingSpinner*/}
          {/*        size="small"*/}
          {/*        color={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}*/}
          {/*      />*/}
          {/*    )}*/}
          {/*  </div>*/}
          {/*)}*/}
        </div>
        {/* Pending-approvals banner lives inside the toolbar row and
            stretches across its free middle, so it takes no extra height —
            appearing neither pushes the content down nor causes a page
            scroll. Suppressed on sample reports (the floating sample-data
            chip uses that space instead). Outside DemoReportOverlay: the
            toolbar actions must never render faded. */}
        <div style={styles.pendingBannerSlot}>
          <PendingApprovalsBanner onReview={() => setReviewModalOpen(true)} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* All biomarkers is the entry into the editable biomarker list —
              affiliate-only, like the other paths that lead there (Add
              results, Manage <system> biomarkers). Clients see the
              report-derived systems view only. */}
          {isAffiliate && (
            <>
              <ActionButton
                label={view === "all" ? "Back to systems" : "All biomarkers"}
                icon={
                  view === "all" ? (
                    <BackArrowIcon color={theme.colors_ds.PRIMARY_ICON_MAIN} />
                  ) : (
                    <ListIcon color={theme.colors_ds.PRIMARY_ICON_MAIN} />
                  )
                }
                onClick={() => {
                  if (view === "all") {
                    setView("systems");
                    return;
                  }
                  // "All biomarkers" always opens unscoped — drop any system
                  // selected in the rail so the list shows every biomarker,
                  // and close whatever the side panel was showing for it. (The
                  // Manage-biomarkers path below deliberately keeps both.)
                  dispatch(platformActions.setSelectedSystem(0));
                  dispatch(platformActions.setSelectedActiveConnection(0));
                  closePanel();
                  setView("all");
                }}
              />
              <AddResultsMenu
                onUpload={() => setExtractorModalOpen(true)}
                onSync={() => setSyncModalOpen(true)}
                onSyncEhr={() => setEhrModalOpen(true)}
              />
            </>
          )}
        </div>
      </div>
      <div
        style={{
          ...styles.container,
          height: "calc(100vh - 158px)",
        }}
      >
        {/* The rail is navigation — never faded; sample score badges inside
            handle their own demo treatment (faded in the systems view,
            hidden while All biomarkers is open). */}
        <ScoreScreenSidebar
          demoBadgeMode={view === "all" ? "hidden" : "faded"}
        />
        {view === "all" ? (
          // The client's real, editable biomarkers — never faded.
          <AllBiomarkersView />
        ) : (
          // Report-derived sample preview (detail column + body map).
          <DemoReportOverlay
            style={{
              flex: 1,
              display: "flex",
              gap: "14px",
              minHeight: 0,
              minWidth: 0,
            }}
          >
            {/* Energy & Training Zones has no body map (as in the
                consultation): its zones take the detail and body-map
                columns. */}
            {selectedSystem === ENERGY_TRAINING_SYSTEM_ID ? (
              <EnergyTrainingSystem />
            ) : (
              <>
                {selectedSystem !== 0 && (
                  <SystemDetails onManageBiomarkers={() => setView("all")} />
                )}
                <BodyMapAndServicesPanel />
              </>
            )}
          </DemoReportOverlay>
        )}
      </div>
      {isAffiliate && (
        <>
          <BiomarkerExtractorModal
            extractorModalOpen={extractorModalOpen}
            setExtractorModalOpen={setExtractorModalOpen}
            onReviewScores={() => setReviewModalOpen(true)}
          />
          <InBodySyncModal
            open={syncModalOpen}
            onClose={() => setSyncModalOpen(false)}
            onReviewScores={() => setReviewModalOpen(true)}
          />
          <KeragonSyncModal
            open={ehrModalOpen}
            onClose={() => setEhrModalOpen(false)}
            onReviewScores={() => setReviewModalOpen(true)}
          />
          {reviewModalOpen && subjectId && (
            <ReviewHealthScoresModal
              open={reviewModalOpen}
              onClose={() => setReviewModalOpen(false)}
              clientId={subjectId}
              userRole={AuthUser.role}
              clientName={clientName || ""}
              onSaveSuccess={() => {
                setReviewModalOpen(false);
                // The modal's save clears dataPlatformServer — refetch the
                // report so the rail and detail column repopulate with the new
                // approval state instead of rendering empty.
                ASRefreshResource("report", subjectId, AuthUser.role);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

const ListIcon = ({ color }: { color: string }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 6h13M8 12h13M8 18h13"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="4" cy="6" r="1.4" fill={color} />
    <circle cx="4" cy="12" r="1.4" fill={color} />
    <circle cx="4" cy="18" r="1.4" fill={color} />
  </svg>
);

const BackArrowIcon = ({ color }: { color: string }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 12H5m0 0l6-6m-6 6l6 6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default HealthOverviewScreen;

const styles = {
  screenRoot: {
    position: "relative" as "relative",
  },
  // Centered in the toolbar row's empty middle; floats above the content so
  // it takes no layout space (no jump when it appears/disappears). The solid
  // background keeps the chip readable over whatever it overlaps.
  demoMessageFloat: (theme: Theme) => ({
    position: "absolute" as "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    top: "4px",
    zIndex: 10,
    backgroundColor: theme.colorTokens.W[10],
    borderRadius: "8px",
  }),
  // The toolbar's middle: the banner stretches through all of it. Collapses
  // to nothing when the banner doesn't render.
  pendingBannerSlot: {
    flex: 1,
    display: "flex",
    margin: "0 24px",
    minWidth: 0,
  },
  toolbar: {
    paddingBottom: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  container: {
    width: "100%",
    position: "relative" as "relative",
    display: "flex",
    gap: "14px",
  },
};
